import React, { useContext, useState, useEffect } from "react";
import axios from "axios";
import "./style.scss";

// Components and icons
import Navbar from "../../components/navbar/Navbar";
import Person from "@material-ui/icons/Person";
import SchoolRounded from "@material-ui/icons/SchoolRounded";
import MenuBookRounded from "@material-ui/icons/MenuBookRounded";
import Email from "@material-ui/icons/Email";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import Lock from "@material-ui/icons/Lock";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import { Button } from "@material-ui/core";
import { LoadingButton } from "@mui/lab";

// Context and utilities
import { AuthContext } from "../../context/authContext/AuthContext";
import { updateUser } from "../../context/authContext/apiCalls";
import dummyProfilePic from "../../assets/dummyProfilePic.png";

function UserUpdate() {
  const { user, isFetching, dispatch } = useContext(AuthContext);

  const [userInfo, setUserInfo] = useState({
    fullname: user?.fullname || "",
    email: user?.email || "",
    course: user?.course || "",
    semester: user?.semester || "",
    profilePic: user?.profilePic || "",
    password: "", // New password (optional)
  });
  const [newProfilePic, setNewProfilePic] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const isAdmin = !!user?.isAdmin;
  const isTeacher = !!user?.isTeacher;
  const isStudent = !isTeacher;
  const restrictedSelfEdit = !isAdmin; // students and normal teachers
  const restrictionMessage = isStudent
    ? "Profile updates for name, email, course, and semester are managed by your teacher. Please contact your teacher to request changes."
    : (!isAdmin
      ? "Profile updates for name, email, course, and semester are managed by the default teacher. Please contact the default teacher to request changes."
      : "");

  useEffect(() => {
    // Sync when global user changes (after successful update)
    if (user) {
      setUserInfo({
        fullname: user.fullname || "",
        email: user.email || "",
        course: user.course || "",
        semester: user.semester || "",
        profilePic: user.profilePic || "",
        password: "", // do not keep old/new password in state
      });
    }
  }, [user]);

  // Handles input changes for user fields
  const handleChange = ({ target: { name, value } }) => {
    setErrorMsg("");
    setSuccessMsg("");
    setUserInfo({ ...userInfo, [name]: value });
  };

  // Handles profile picture file selection and preview
  const handleProfilePicChange = ({ target: { files } }) => {
    if (files && files[0]) {
      setNewProfilePic(files[0]);
      setUserInfo({ ...userInfo, profilePic: URL.createObjectURL(files[0]) });
      setErrorMsg("");
      setSuccessMsg("");
    }
  };

  // Uploads image file to Cloudinary and returns the image data
  const uploadToCloudinary = async (file) => {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
    const preset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || "unsigned_upload";
    if (!cloudName) {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post("/api/upload/profile-pic", fd, { headers: { token: `Bearer ${user.accessToken}`, Authorization: `Bearer ${user.accessToken}` } });
      return res.data;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", preset);
    // Optional: put in folder
    formData.append("folder", "profile_pics");
    const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/upload`, { method: "POST", body: formData });
    if (!res.ok) {
      let errMsg = `Cloudinary status ${res.status}`;
      try {
        const errJson = await res.json();
        errMsg += `: ${errJson.error?.message || JSON.stringify(errJson)}`;
      } catch (_) {}
      throw new Error(errMsg);
    }
    return res.json();
  };

  // Handles user data submission, including optional image upload
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    let payload = { ...userInfo };

    // Do not send empty password
    if (!payload.password) delete payload.password;

    // If blob preview (from createObjectURL) and no successful upload yet, reset to original so we don't send blob URL
    if (payload.profilePic.startsWith("blob:")) {
      payload.profilePic = user?.profilePic || "";
    }

    // Enforce client-side restriction: strip restricted fields if user is not admin
    if (restrictedSelfEdit) {
      delete payload.fullname;
      delete payload.email;
      delete payload.course;
      delete payload.semester;
    }

    // Try uploading image separately but don't block text update if it fails
    if (newProfilePic) {
      try {
        const imgData = await uploadToCloudinary(newProfilePic);
        if (imgData?.secure_url) payload.profilePic = imgData.secure_url;
      } catch (imgErr) {
        console.warn("Image upload failed, continuing without new image", imgErr.message);
        setErrorMsg(`Image upload failed: ${imgErr.message}. Details updated without new photo.`);
      }
    }

    try {
      const updated = await updateUser(user, payload, dispatch);
      if (updated) {
        setUserInfo({
          fullname: updated.fullname || userInfo.fullname || "",
          email: updated.email || userInfo.email || "",
          course: updated.course || userInfo.course || "",
          semester: updated.semester || userInfo.semester || "",
          profilePic: updated.profilePic || "",
          password: "", // clear after update
        });
        setSuccessMsg("Profile updated successfully");
      }
      setNewProfilePic(null);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) {
        setErrorMsg(err.response.data || restrictionMessage || "Not allowed.");
      } else {
        if (!errorMsg) setErrorMsg("Update failed. Try again.");
      }
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <div className="container">
      {/* Top navigation bar */}
      <Navbar />

      <div className="user-page">
        {/* Profile picture section */}
        <div className="userimg">
          <img src={userInfo.profilePic || dummyProfilePic} alt="profile" />

          {/* Upload button with camera icon */}
          <div className="upload">
            <Button
              variant="text"
              component="label"
              disableElevation
              endIcon={<PhotoCamera className="cam" />}
            >
              <input type="file" hidden accept="image/*" onChange={handleProfilePicChange} />
            </Button>
          </div>
        </div>

        {/* Profile update form */}
        <h2>Profile Update</h2>
        
        {/* Simple info alert for restricted users */}
        {restrictedSelfEdit && (
          <div style={{ 
            maxWidth: 600, 
            margin: '20px auto', 
            padding: '14px 18px',
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}>
            <InfoOutlined style={{ color: '#856404', fontSize: 28 }} />
            <div style={{ flex: 1 }}>
              <p style={{ 
                margin: 0, 
                fontSize: 14, 
                color: '#856404',
                lineHeight: 1.5,
                fontWeight: 500
              }}>
                <strong>Note:</strong> {restrictionMessage}
              </p>
            </div>
          </div>
        )}

        <form className="edit-user" onSubmit={handleSubmit}>
          {/* Full Name */}
          <div className="user-input">
            <input
              type="text"
              name="fullname"
              placeholder="Enter your full name"
              value={userInfo.fullname}
              onChange={handleChange}
              disabled={restrictedSelfEdit}
            />
            <Person className="icon" />
          </div>

          {/* Email */}
          <div className="user-input">
            <input
              type="text"
              name="email"
              placeholder="Enter your email"
              value={userInfo.email}
              onChange={handleChange}
              disabled={restrictedSelfEdit}
            />
            <Email className="icon" />
          </div>

          {/* New Password (optional) */}
          <div className="user-input">
            <input
              type="password"
              name="password"
              placeholder="Enter new password (optional)"
              value={userInfo.password}
              onChange={handleChange}
            />
            <Lock className="icon" />
          </div>

          {/* Course */}
          <div className="user-input">
            <input
              type="text"
              name="course"
              placeholder="Enter your course"
              value={userInfo.course}
              onChange={handleChange}
              disabled={restrictedSelfEdit}
            />
            <SchoolRounded className="icon" />
          </div>

          {/* Semester */}
          <div className="user-input">
            <input
              type="text"
              name="semester"
              placeholder="Enter your semester"
              value={userInfo.semester}
              onChange={handleChange}
              disabled={restrictedSelfEdit}
            />
            <MenuBookRounded className="icon" />
          </div>

          {/* Save button with loading state */}
          <div className="save-psswd">
            <LoadingButton
              type="submit"
              variant="contained"
              className="btn"
              style={{ textTransform: "none" }}
              loading={updateLoading || isFetching}
              loadingPosition="center"
            >
              Save
            </LoadingButton>
          </div>

          {/* Error and success messages */}
          {errorMsg && <p style={{ color: "red", marginTop: 10 }}>{errorMsg}</p>}
          {successMsg && <p style={{ color: "green", marginTop: 10 }}>{successMsg}</p>}
        </form>
      </div>
    </div>
  );
}

export default UserUpdate;
